import React, { useEffect } from 'react';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
} from '@backstage/plugin-techdocs-react';

const STYLE_ID = 'custom-techdocs-sidebar';

/**
 * CSS injected directly into the TechDocs Shadow DOM.
 *
 * TechDocs renders MkDocs HTML inside a Shadow DOM (TechDocsShadowDom).
 * Global CSS in styles.css cannot cross the Shadow DOM boundary, so all
 * .md-sidebar / .md-content overrides must be injected here instead.
 *
 */
const SIDEBAR_FIX_CSS = `

  .md-sidebar--primary,
  .md-sidebar--secondary {
    position: relative !important;
    top: 0 !important;
    height: 80vh !important;
    overflow: hidden !important;
  }

  .md-sidebar__scrollwrap {
    position: relative !important;
    top: auto !important;
    bottom: auto !important;
    height: 100% !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    scrollbar-width: none !important;
  }

  .md-sidebar__scrollwrap:hover {
    scrollbar-width: thin !important;
  }

  .md-sidebar__scrollwrap::-webkit-scrollbar {
    width: 0 !important;
  }

  .md-sidebar__scrollwrap:hover::-webkit-scrollbar {
    width: 4px !important;
  }

  .md-sidebar__scrollwrap:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2) !important;
    border-radius: 4px !important;
  }

  .md-content {
    margin-left: 0 !important;
  }

  .md-footer {
    padding-bottom: 1rem !important;
  }
`;

const SidebarStyleFixComponent = () => {
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;
    if (shadowRoot.querySelector(`#${STYLE_ID}`)) return undefined;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = SIDEBAR_FIX_CSS;

    const head = shadowRoot.querySelector('head');
    if (head) {
      head.appendChild(style);
    } else {
      shadowRoot.appendChild(style);
    }

    return () => {
      style.remove();
    };
  }, [shadowRoot]);

  return null;
};

const techDocsSidebarPlugin = createPlugin({ id: 'techdocs-sidebar-style-fix' });

export const TechDocsSidebarStyleAddon = techDocsSidebarPlugin.provide(
  createTechDocsAddonExtension({
    name: 'SidebarStyleFix',
    location: TechDocsAddonLocations.Content,
    component: SidebarStyleFixComponent,
  }),
) as unknown as () => JSX.Element | null;

